import { useEffect, useState } from 'react'
import { fetchAccountOnboarding } from '../api.js'

/**
 * What a run form needs to know about the account it will dispatch into.
 *
 * Both screens that offer a run ask the same two things of the same endpoint —
 * which regions exist, and which of them hand the runner their own registry
 * credentials — so it is one hook rather than the same effect written twice.
 * The second question arrived with IM-118 and was the point at which the
 * duplication stopped being harmless: a screen that forgot to ask it would
 * silently keep demanding a registry key that is no longer needed.
 *
 * ## Why it is gated on `opened`
 *
 * This is a cross-account read, and a customer screen has one run panel per
 * account. A closed `<details>` hides its children and React mounts them all
 * the same — the IM-83 lesson — so an ungated effect would fan out one call per
 * account for panels nobody opened. `opened` is the caller's own "someone has
 * actually asked for this".
 *
 * ## Why a failure is silent
 *
 * The answer is a convenience, and the failure is usually ours. Undefined means
 * nobody has looked, which every caller treats as the cautious answer: type a
 * region, and bring a registry key. A panel someone opened to start a run
 * should not lead with a complaint about a list.
 */
export interface AccountRunFacts {
  /** The regions this account is set up in. Undefined means unknown. */
  readonly regions?: readonly string[]
  /**
   * The subset whose dispatcher supplies the registry key (IM-118).
   *
   * Per region because the secret lives in the dispatcher stack, which is
   * deployed once per region — an account can genuinely supply it in one and
   * not another.
   */
  readonly registryFromAccount?: readonly string[]
  /**
   * The installer image each region's dispatcher runs, by region (IM-196).
   *
   * So the run form can compare the chart version typed with the installer
   * that will run it, on the keystroke: the release pipeline builds both from
   * one version. A region absent here is one whose image could not be read.
   */
  readonly installerImages?: Readonly<Record<string, string>>
}

export const useAccountRunFacts = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  opened: boolean,
): AccountRunFacts => {
  const [facts, setFacts] = useState<AccountRunFacts>({})

  useEffect(() => {
    if (!opened) return
    let live = true
    void fetchAccountOnboarding(baseUrl, tenantId, accountKey)
      .then((answer) => {
        if (!live || answer.account === undefined) return
        setFacts({
          regions: answer.account.regions.map((r) => r.region),
          registryFromAccount: answer.account.regions
            .filter((r) => r.suppliesRegistryKey)
            .map((r) => r.region),
          installerImages: Object.fromEntries(
            answer.account.regions.flatMap((r) =>
              r.runnerImage === undefined ? [] : [[r.region, r.runnerImage] as const],
            ),
          ),
        })
      })
      .catch(() => {
        // Left unknown. See the note above.
      })
    return () => {
      live = false
    }
  }, [opened, baseUrl, tenantId, accountKey])

  return facts
}
